import React, { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './ImageGalleryViewer.css';

// Minimum downward drag before we dismiss (pixels)
const SWIPE_DISMISS_THRESHOLD = 80;
// Minimum horizontal drag before we turn the page (pixels)
const SWIPE_PAGE_THRESHOLD = 60;

// Zoom bounds. 1 = "fits the viewport" (the image is already capped by
// max-width/max-height in CSS), so zooming out below that only adds letterboxing.
const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.4;

const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

export interface GalleryImage {
  src: string;
  alt?: string;
  /** Caption under the thumbnail (e.g. "Page 2"). Falls back to the index. */
  label?: string;
}

export interface ImageGalleryViewerProps {
  images: GalleryImage[];
  /** Page shown on open. Clamped into range. */
  initialIndex?: number;
  onClose: () => void;
  /** Shown top-left, e.g. "Uploaded solution". */
  title?: string;
  /** Extra toolbar controls (e.g. a link to the original PDF). Style with `.iv-btn`. */
  toolbarExtra?: ReactNode;
}

/**
 * Fullscreen viewer for an ordered set of images — a thumbnail rail plus the
 * selected page at full size, with zoom / rotate / pan on the selection.
 *
 * Built for multi-page documents (a scanned solution, a rasterized PDF), where
 * the pages are stored as an array of per-page images rather than one composite.
 * `ImageViewer` is the single-image case and delegates here.
 *
 * Zoom and pan reset on page turn — they describe how you're looking at *one*
 * page. Rotation persists, because a sideways scan is sideways on every page.
 */
export function ImageGalleryViewer({ images, initialIndex = 0, onClose, title, toolbarExtra }: ImageGalleryViewerProps) {
  const count = images.length;
  const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0)));
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  // Pan offset in screen pixels, relative to the centred position.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState(0);
  const [swipeX, setSwipeX] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  // Set when a pan actually moved, so releasing the mouse over the backdrop
  // doesn't also read as "clicked outside → close".
  const didPan = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);

  const zoomed = scale > 1;
  const multi = count > 1;
  const current = images[index];

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const goTo = useCallback((next: number) => {
    if (next < 0 || next >= count) return;
    setIndex(next);
    resetView();
  }, [count, resetView]);

  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);

  /**
   * Zoom about a fixed point. With `transform: translate(o) rotate(r) scale(s)`
   * a point p lands at `o + R·s·p`, so holding the point under the cursor still
   * gives `o1 = c - (s1/s0)(c - o0)` — the rotation term cancels and the same
   * formula works at any angle. `c` is relative to the container centre;
   * omitting it zooms about the middle (what the toolbar buttons want).
   */
  const zoomTo = useCallback((nextScale: number, c?: { x: number; y: number }) => {
    setScale(prevScale => {
      const s1 = clampScale(nextScale);
      if (s1 === prevScale) return prevScale;
      if (s1 === 1) {
        setOffset({ x: 0, y: 0 });
      } else if (c) {
        setOffset(o => ({
          x: c.x - (s1 / prevScale) * (c.x - o.x),
          y: c.y - (s1 / prevScale) * (c.y - o.y),
        }));
      }
      return s1;
    });
  }, []);

  const zoomIn = useCallback(() => zoomTo(scale * ZOOM_STEP), [scale, zoomTo]);
  const zoomOut = useCallback(() => zoomTo(scale / ZOOM_STEP), [scale, zoomTo]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn(); }
    else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomOut(); }
    else if (e.key === '0') { e.preventDefault(); resetView(); }
    // Arrows turn the page rather than pan — panning is a drag gesture, and a
    // multi-page document wants the arrows for what they say on the key.
    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
  }, [onClose, zoomIn, zoomOut, resetView, prev, next]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  // Keep the selected thumbnail in view when the page changes by keyboard/swipe.
  useEffect(() => {
    railRef.current?.querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index]);

  // Wheel zoom, anchored at the pointer so the detail under the cursor stays put.
  // Registered non-passively because the page behind must not scroll.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const c = {
        x: e.clientX - (rect.left + rect.width / 2),
        y: e.clientY - (rect.top + rect.height / 2),
      };
      zoomTo(scale * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP), c);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scale, zoomTo]);

  const rotateLeft = () => setRotation(r => r - 90);
  const rotateRight = () => setRotation(r => r + 90);

  // ── Mouse drag to pan (only meaningful once zoomed in) ──────────────────
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!zoomed || e.button !== 0) return;
    e.preventDefault();
    panStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    didPan.current = false;
    setIsPanning(true);
  };

  useEffect(() => {
    if (!isPanning) return;
    const onMove = (e: MouseEvent) => {
      const s = panStart.current;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didPan.current = true;
      setOffset({ x: s.ox + dx, y: s.oy + dy });
    };
    const onUp = () => { setIsPanning(false); panStart.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPanning]);

  // ── Touch: one finger pans when zoomed; otherwise the dominant axis decides
  // — sideways turns the page, downward dismisses. Pinch stays native
  // (touch-action: pinch-zoom), so mobile keeps the zoom it already had.
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      touchStart.current = null;
      panStart.current = null;
      setDragOffset(0);
      setSwipeX(0);
      return;
    }
    if (zoomed) {
      panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, ox: offset.x, oy: offset.y };
    } else {
      touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    if (zoomed && panStart.current) {
      const s = panStart.current;
      setOffset({ x: s.ox + (e.touches[0].clientX - s.x), y: s.oy + (e.touches[0].clientY - s.y) });
      return;
    }
    const s = touchStart.current;
    if (!s) return;
    const dx = e.touches[0].clientX - s.x;
    const dy = e.touches[0].clientY - s.y;
    if (multi && Math.abs(dx) > Math.abs(dy)) {
      setSwipeX(dx);
      setDragOffset(0);
    } else {
      setDragOffset(Math.max(0, dy));
      setSwipeX(0);
    }
  };

  const handleTouchEnd = () => {
    // Only the un-zoomed, single-finger drag dismisses or turns — panning a
    // zoomed image must not do either.
    if (!zoomed) {
      if (Math.abs(swipeX) > SWIPE_PAGE_THRESHOLD) {
        if (swipeX < 0) next(); else prev();
      } else if (dragOffset > SWIPE_DISMISS_THRESHOLD) {
        onClose();
      }
    }
    setDragOffset(0);
    setSwipeX(0);
    touchStart.current = null;
    panStart.current = null;
  };

  const overlayStyle = dragOffset > 0
    ? {
        transform: `translateY(${dragOffset}px)`,
        opacity: Math.max(0.3, 1 - dragOffset / 400),
        transition: 'none',
      }
    : undefined;

  if (count === 0) return null;

  return createPortal(
    <div
      className={`iv-overlay${multi ? ' ivg-multi' : ''}`}
      onClick={(e) => {
        // A pan that ended over the backdrop is not a click-outside.
        if (didPan.current) { didPan.current = false; return; }
        if (e.target === e.currentTarget) onClose();
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={overlayStyle}
    >
      {(title || multi) && (
        <div className="ivg-heading">
          {title && <span className="ivg-title">{title}</span>}
          {multi && <span className="ivg-counter">{index + 1} / {count}</span>}
        </div>
      )}

      <div className="iv-toolbar">
        {toolbarExtra}
        <button className="iv-btn" onClick={zoomOut} disabled={scale <= MIN_SCALE} title="Zoom out (−)" aria-label="Zoom out">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M8 11h6" />
          </svg>
        </button>
        <button
          className="iv-btn iv-zoom-level"
          onClick={resetView}
          disabled={!zoomed}
          title="Reset zoom (0)"
          aria-label={`Zoom ${Math.round(scale * 100)} percent. Reset zoom`}
        >
          {Math.round(scale * 100)}%
        </button>
        <button className="iv-btn" onClick={zoomIn} disabled={scale >= MAX_SCALE} title="Zoom in (+)" aria-label="Zoom in">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M8 11h6M11 8v6" />
          </svg>
        </button>
        <span className="iv-toolbar-sep" aria-hidden="true" />
        <button className="iv-btn" onClick={rotateLeft} title="Rotate left" aria-label="Rotate left">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>
        <button className="iv-btn" onClick={rotateRight} title="Rotate right" aria-label="Rotate right">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
          </svg>
        </button>
        <button className="iv-btn iv-close-btn" onClick={onClose} title="Close" aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {multi && (
        <>
          <button className="iv-btn ivg-nav ivg-nav-prev" onClick={prev} disabled={index === 0} title="Previous page (←)" aria-label="Previous page">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button className="iv-btn ivg-nav ivg-nav-next" onClick={next} disabled={index === count - 1} title="Next page (→)" aria-label="Next page">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      <div
        ref={containerRef}
        className={`iv-image-container${zoomed ? ' iv-zoomed' : ''}${isPanning ? ' iv-panning' : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={() => (zoomed ? resetView() : zoomTo(2))}
      >
        <img
          key={current.src}
          src={current.src}
          alt={current.alt ?? current.label ?? `Page ${index + 1}`}
          className="iv-image"
          style={{
            transform: `translate(${offset.x + swipeX}px, ${offset.y}px) rotate(${rotation}deg) scale(${scale})`,
            // Animating while dragging or wheel-zooming feels laggy; only the
            // discrete button presses should ease.
            transition: isPanning || swipeX !== 0 ? 'none' : undefined,
          }}
          draggable={false}
        />
      </div>

      {multi && (
        <div
          className="ivg-rail"
          ref={railRef}
          onClick={(e) => e.stopPropagation()}
          // The rail scrolls sideways under the same finger the overlay reads as
          // a page-turn swipe — let it keep its own gesture.
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={img.src}
              type="button"
              className={`ivg-thumb${i === index ? ' ivg-thumb-active' : ''}`}
              data-selected={i === index}
              onClick={() => goTo(i)}
              title={img.label ?? `Page ${i + 1}`}
              aria-label={`Show ${img.label ?? `page ${i + 1}`}`}
              aria-current={i === index}
            >
              <img src={img.src} alt="" loading="lazy" draggable={false} />
              <span className="ivg-thumb-label">{img.label ?? i + 1}</span>
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
